import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { supabase } from '../lib/supabase';
import { LoginForm, type LoginTab } from '@/components/auth/LoginForm';
import { Route } from '../routes/login';
import { LoginContent } from '@/content/LoginContent';

/**
 * Login page orchestrator component
 * Manages all state, Supabase auth calls, and router navigation
 * Delegates UI rendering to presentational LoginForm component
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const navigate = useNavigate();
  const { tab: activeTab, redirect } = Route.useSearch();

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message);
        setLoading(false);
        return;
      }

      // Navigation is handled by main.tsx after auth state updates
    } catch (err) {
      setError(LoginContent.errors.unexpected);
      setLoading(false);
    }
  };

  const handleOtpRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false, // Prevent signup for uninvited users
        },
      });

      if (otpError) {
        setError(otpError.message);
        setLoading(false);
        return;
      }

      // Success - code sent
      setCodeSent(true);
      setLoading(false);
    } catch (err) {
      setError(LoginContent.errors.unexpected);
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const {
        data: { session },
        error: verifyError,
      } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email',
      });

      if (verifyError) {
        setError(verifyError.message);
        setLoading(false);
        return;
      }

      // Navigation is handled by main.tsx after auth state updates
      if (!session) {
        setError(LoginContent.errors.unexpected);
        setLoading(false);
      }
    } catch (err) {
      setError(LoginContent.errors.unexpected);
      setLoading(false);
    }
  };

  const handleTabChange = (tab: LoginTab) => {
    setError(null);
    navigate({
      to: '/login',
      search: {
        tab,
        redirect,
      },
    });
  };

  const handleResetCode = () => {
    setCodeSent(false);
    setOtpCode('');
  };

  return (
    <LoginForm
      email={email}
      password={password}
      otpCode={otpCode}
      error={error}
      loading={loading}
      codeSent={codeSent}
      activeTab={activeTab}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onOtpCodeChange={setOtpCode}
      onTabChange={handleTabChange}
      onPasswordSubmit={handlePasswordSubmit}
      onOtpRequest={handleOtpRequest}
      onOtpVerify={handleOtpVerify}
      onResetCode={handleResetCode}
    />
  );
}

